import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';
import { useCartStore } from '@/stores/cart-store';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Turnstile } from '@/components/Turnstile';
import { Honeypot } from '@/components/Honeypot';
import { IS_RUMIO } from '@/config/site';
import { useSettings } from '@/hooks/useProducts';
import { isSafeReturnUrl } from '@/lib/safe-return-url';
import { resolvePostAuthDest } from '@/lib/post-auth-dest';
import { finalizeCompanionHandoff, readHandoffParams } from '@/lib/companion-handoff';
import SocialLoginButtons from '@/components/auth/SocialLoginButtons';
import type { SocialProviderId } from '@/lib/auth';

export default function Login() {
  const { t } = useTranslation('auth');
  // Tenant-managed Turnstile site key (PR #113 admin UI writes to
  // site_settings.turnstile_site_key). Falls back to build-time env var
  // for tenants that haven't migrated yet — keeps existing flows working
  // during the Phase 3 → Phase 5 rollout window.
  const { data: settingsData } = useSettings();
  const turnstileSiteKey =
    (settingsData?.settings?.turnstile_site_key as string | undefined) ||
    import.meta.env.VITE_TURNSTILE_SITE_KEY ||
    '';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);

  // Companion-surface handoff (mobile + extension, contract v1 §11). When the
  // URL has these params we treat login as a stepping stone: after auth, the
  // browser is redirected back to the companion's deep-link with the JWT.
  const handoff = readHandoffParams(searchParams);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const [handoffPending, setHandoffPending] = useState(false);
  // `?redirect=` is honored for cross-origin returns from installed apps —
  // e.g. an unauthenticated invitee opening a meal-planner invite link
  // gets bounced here, signs in, and lands back on the invite URL. We
  // require an absolute URL whose origin is on the same eTLD+1 as this
  // page so a malicious link can't bounce users off-platform. Anything
  // that doesn't pass the safety check falls through to the normal
  // post-login destination, which is resolved at submit time (below) once
  // the freshly-fetched user — and its subscription state — is known.
  const redirectParam = searchParams.get('redirect');
  const safeRedirect = isSafeReturnUrl(redirectParam);
  const deepLinkDest = safeRedirect ?? (location.state as any)?.from?.pathname ?? null;

  const login = useAuthStore((s) => s.login);
  const loginWithProvider = useAuthStore((s) => s.loginWithProvider);
  const isLoading = useAuthStore((s) => s.isLoading);
  const mergeGuestCart = useCartStore((s) => s.mergeGuestCart);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError(t('login.errors.missingFields'));
      return;
    }

    try {
      await login(email, password, turnstileToken);
      await mergeGuestCart();
      // If this login was launched by the mobile/extension companion app,
      // hand control back to it instead of going to /account or /admin.
      if (handoff) {
        await completeHandoff();
        return;
      }
      // Resolve the default destination from the freshly-fetched user (the
      // `user` selector above is stale on a cold login). A deep-link target
      // (?redirect= / location.state.from) still wins when present.
      const freshUser = useAuthStore.getState().user;
      // `?plan=` lets a returning customer come straight from a pricing tier
      // into checkout instead of bouncing off the marketing page again.
      navigateAfterLogin(
        deepLinkDest ?? resolvePostAuthDest(freshUser, searchParams.get('plan')),
      );
    } catch (err: any) {
      setError(err?.message || t('login.errors.generic'));
    }
  }

  function navigateAfterLogin(destination: string) {
    // Cross-origin redirects (returned safe by isSafeReturnUrl above) need
    // window.location, not react-router. Same-origin paths can use
    // react-router so we don't blow out the SPA's history and lose
    // app-state for in-flight requests.
    if (/^https?:\/\//i.test(destination)) {
      window.location.assign(destination);
      return;
    }
    navigate(destination, { replace: true });
  }

  // Called after auth completes (either via this page's form, or by a social
  // login that returned to /auth/callback and bounced us back here as
  // already-signed-in). Calls the backend finalize endpoint and navigates
  // the OS browser to the deep-link URL — which closes the in-app browser
  // tab on iOS/Android and re-enters the companion app.
  async function completeHandoff() {
    if (!handoff) return;
    setHandoffError(null);
    setHandoffPending(true);
    try {
      const { redirect_url } = await finalizeCompanionHandoff(handoff);
      // window.location.assign keeps the back-button behaviour reasonable;
      // replace() would orphan the login page in history.
      window.location.assign(redirect_url);
    } catch (err: any) {
      setHandoffError(err?.message || t('login.errors.generic'));
      setHandoffPending(false);
    }
  }

  // If the user is already signed in when they land here with a handoff
  // query (e.g., reopened the deep-link while a session was still valid),
  // skip the form and finalize immediately.
  useEffect(() => {
    if (handoff && user && !handoffPending && !handoffError) {
      void completeHandoff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff?.app, user?.id]);

  // Already signed in, sent here with an explicit, safety-checked `?redirect=`
  // — typically an embedded app's "Sign in" or "Save my progress", which
  // cannot see the host session and always asks. Showing the form again to
  // someone who is signed in is a dead end; go where they were going. Only the
  // explicit param counts: a plain visit to /login still shows the form.
  useEffect(() => {
    if (!handoff && user && safeRedirect) navigateAfterLogin(safeRedirect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, safeRedirect]);

  // Social logins round-trip through the IdP and /auth/callback, which loses
  // the in-memory `?redirect=` target. Stash it (mirrors Signup's
  // `pending_ref_code`) so AuthCallback can return the invitee to their invite
  // URL after OAuth. Only stash an explicit, safety-checked redirect.
  function stashRedirectForOAuth() {
    if (!safeRedirect) return;
    try {
      sessionStorage.setItem('pending_login_redirect', safeRedirect);
    } catch {
      /* sessionStorage unavailable (private mode quota) — silent */
    }
  }

  async function handleSocialLogin(provider: SocialProviderId) {
    try {
      stashRedirectForOAuth();
      await loginWithProvider(provider);
    } catch (err: any) {
      setError(err?.message || t(`login.errors.${provider}Failed`));
    }
  }

  return (
    <main className="flex flex-1 items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-ink">{t('login.heading')}</h1>
          <p className="mt-2 text-ink-soft">
            {handoff
              ? t('login.subtitleCompanion', {
                  defaultValue: 'Sign in to continue to {{app}}',
                  app: handoff.app,
                })
              : IS_RUMIO
                ? t('login.subtitleWorkspace')
                : t('login.subtitleShop')}
          </p>
        </div>

        {/* Companion handoff status: shown while we're round-tripping the
            finalize call (after auth success), or if it fails. */}
        {handoff && (handoffPending || handoffError) && (
          <div
            className={cn(
              'mt-6 rounded-lg border p-3 text-sm',
              handoffError
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-line bg-surface-soft text-ink-soft',
            )}
            role={handoffError ? 'alert' : 'status'}
          >
            {handoffError ?? t('login.companionHandingOff', { defaultValue: 'Returning to the app…' })}
          </div>
        )}

        <div className="mt-8 rounded-xl border border-line bg-surface p-6 shadow-sm sm:p-8">
          {/* Error alert */}
          {error && (
            <div
              className="mb-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* Social sign-in. Renders only providers the platform has
              credentials for AND this tenant enabled; the divider goes with
              them, so a tenant with social login off sees a plain email form. */}
          <SocialLoginButtons
            namespace="login"
            disabled={isLoading}
            onProvider={handleSocialLogin}
            divider={
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-line" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-surface px-4 text-ink-muted">{t('login.orWithEmail')}</span>
                </div>
              </div>
            }
          />

          {/* Email/password form */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4 relative">
            <Honeypot />
            <Input
              label={t('login.emailLabel')}
              type="email"
              autoComplete="email"
              required
              placeholder={t('login.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <div>
              <Input
                label={t('login.passwordLabel')}
                type="password"
                autoComplete="current-password"
                required
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className="mt-1 text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-sage-700 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>
            </div>

            <Turnstile
              siteKey={turnstileSiteKey}
              onVerify={setTurnstileToken}
            />

            <Button
              type="submit"
              isLoading={isLoading}
              disabled={!!turnstileSiteKey && !turnstileToken}
              className="w-full"
              size="lg"
            >
              {t('login.submit')}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-ink-soft">
          {t('login.noAccount')}{' '}
          {/* The way back travels with the visitor. An invitee bounced here by
              /invite/:code who has no account yet is the person most likely
              to press this link; without the redirect they would sign up and
              land on the default page, and the invite they came for is gone. */}
          <Link
            to={safeRedirect ? `/signup?redirect=${encodeURIComponent(safeRedirect)}` : '/signup'}
            className="font-medium text-sage-700 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
          >
            {t('login.createAccount')}
          </Link>
        </p>
      </div>
    </main>
  );
}
